/**
 * PortfolioHeader — the Fleet console's top stat strip (UI-SPEC §1). A framed
 * <StatStrip> band summarizing the whole fleet over the selected window (tokens
 * are the headline — Anthropic: ~80% of cost/perf variance). The shared
 * `portfolioStatItems` builder is reused by the fleet console to render the same
 * KPIs as the fleet table's caption band (variant="table").
 */

import { StatStrip, type StatStripItem } from "../dashboard/stat-strip";
import { formatUsd } from "../../format";

export interface PortfolioStats {
  active_loops: number;
  /** Workflows with a complete role chain — actually executable today. */
  runnable_loops?: number;
  /** Workflows awaiting role authoring — surfaced at /agents/roadmap. */
  blocked_loops?: number;
  total_loops: number;
  runs_24h: number;
  cost_24h: number;
  tokens_24h: number;
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/** The fleet KPIs as StatStrip items — shared by the standalone header and the
 *  fleet table's caption band so both render identically. */
export function portfolioStatItems(
  stats: PortfolioStats,
  rangeLabel = "24h",
): StatStripItem[] {
  const runnable = stats.runnable_loops ?? stats.total_loops;
  const blocked = stats.blocked_loops ?? 0;
  const loopsDescription =
    blocked > 0
      ? `${runnable} runnable · ${blocked} pending roles`
      : `${stats.total_loops} total`;
  return [
    {
      title: "Active workflows",
      value: String(stats.active_loops),
      description: loopsDescription,
    },
    { title: `Runs (${rangeLabel})`, value: String(stats.runs_24h) },
    { title: `Cost (${rangeLabel})`, value: formatUsd(stats.cost_24h) },
    { title: `Tokens (${rangeLabel})`, value: fmtTokens(stats.tokens_24h) },
  ];
}

export function PortfolioHeader({
  stats,
  rangeLabel = "24h",
}: {
  stats: PortfolioStats;
  /** Window the runs/cost/tokens reflect — shown in each stat title. */
  rangeLabel?: string;
}) {
  return (
    <StatStrip items={portfolioStatItems(stats, rangeLabel)} variant="standalone" />
  );
}
